import { useState, useMemo } from "react";
import type { Reservation, BookingSeason, CabinType } from "../../types";
import { nightCount, isActive } from "../../utils";
import { useAdminData } from "../../hooks/useAdminData";

function isPriorityRound(r: Reservation): boolean {
  return r.booking_round != null && r.booking_round !== "open_season";
}

function cabinNights(r: Reservation, cabin: CabinType): number {
  if (!r.cabins.includes(cabin)) return 0;
  return nightCount(r);
}

function daysBetween(start: string, end: string): number {
  const s = new Date(start + "T00:00:00");
  const e = new Date(end + "T00:00:00");
  return Math.max(0, Math.round((e.getTime() - s.getTime()) / 86_400_000));
}

function monthRange(
  year: number,
  month: number,
  seasonStart: string,
  seasonEnd: string
): { start: string; end: string; nights: number } | null {
  const mStart = new Date(year, month, 1);
  const mEnd = new Date(year, month + 1, 1);
  const sStart = new Date(seasonStart + "T00:00:00");
  const sEnd = new Date(seasonEnd + "T00:00:00");

  const effStart = mStart > sStart ? mStart : sStart;
  const effEnd = mEnd < sEnd ? mEnd : sEnd;

  if (effStart >= effEnd) return null;

  const startStr = effStart.toISOString().slice(0, 10);
  const endStr = effEnd.toISOString().slice(0, 10);
  return { start: startStr, end: endStr, nights: daysBetween(startStr, endStr) };
}

function bookedNightsInRange(
  reservations: Reservation[],
  rangeStart: string,
  rangeEnd: string,
  cabin?: CabinType
): number {
  let total = 0;
  for (const r of reservations) {
    if (cabin && !r.cabins.includes(cabin)) continue;
    const rStart = r.start_date > rangeStart ? r.start_date : rangeStart;
    const rEnd = r.end_date < rangeEnd ? r.end_date : rangeEnd;
    if (rStart < rEnd) {
      total += daysBetween(rStart, rEnd);
    }
  }
  return total;
}

function pct(available: number, booked: number): number {
  return available > 0 ? Math.round(((available - booked) / available) * 100) : 0;
}

/** Count Friday + Saturday nights in a date range */
function countWeekendNights(start: string, end: string): number {
  let count = 0;
  const cur = new Date(start + "T00:00:00");
  const endDate = new Date(end + "T00:00:00");
  while (cur < endDate) {
    const day = cur.getDay();
    if (day === 5 || day === 6) count++; // Friday or Saturday
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

/** Count booked weekend nights for reservations in a range, optionally filtered by cabin */
function bookedWeekendNightsInRange(
  reservations: Reservation[],
  rangeStart: string,
  rangeEnd: string,
  cabin?: CabinType
): number {
  let total = 0;
  for (const r of reservations) {
    if (cabin && !r.cabins.includes(cabin)) continue;
    const rStart = r.start_date > rangeStart ? r.start_date : rangeStart;
    const rEnd = r.end_date < rangeEnd ? r.end_date : rangeEnd;
    if (rStart < rEnd) {
      total += countWeekendNights(rStart, rEnd);
    }
  }
  return total;
}

/** Filter active reservations that overlap a season's date range */
function reservationsForSeason(reservations: Reservation[], season: BookingSeason): Reservation[] {
  return reservations.filter(
    (r) => r.start_date < season.open_season_end && r.end_date > season.open_season_start
  );
}

interface MonthAvailability {
  label: string;
  start: string;
  end: string;
  totalNights: number;
  hiltonBooked: number;
  windsorBooked: number;
  availability: number;
  hiltonAvailability: number;
  windsorAvailability: number;
}

interface ShareholderStats {
  name: string;
  priority: number;
  priorityStays: number;
  totalNights: number;
  hiltonNights: number;
  windsorNights: number;
  months: {
    label: string;
    nights: number;
    hiltonNights: number;
    windsorNights: number;
  }[];
}

const CABINS: { key: CabinType; label: string }[] = [
  { key: "hilton", label: "Hilton" },
  { key: "windsor", label: "Windsor" },
];

export function AnalyticsAdmin() {
  const { reservations, season, seasons, shareholders, loading, error } = useAdminData();
  const [selectedYear, setSelectedYear] = useState<number | null>(null);

  const sortedSeasons = useMemo(
    () => [...seasons].sort((a, b) => b.year - a.year),
    [seasons]
  );

  const selectedSeason = useMemo(() => {
    if (selectedYear != null) return sortedSeasons.find((s) => s.year === selectedYear) || null;
    return season;
  }, [selectedYear, sortedSeasons, season]);

  const activeForSeason = useMemo(() => {
    const allActive = reservations.filter(isActive);
    if (!selectedSeason) return allActive;
    return reservationsForSeason(allActive, selectedSeason);
  }, [reservations, selectedSeason]);

  const months = useMemo(() => {
    if (!selectedSeason) return [];
    const result: MonthAvailability[] = [];
    const end = new Date(selectedSeason.prime_season_end + "T00:00:00");

    let cur = new Date(new Date(selectedSeason.prime_season_start + "T00:00:00").getFullYear(), new Date(selectedSeason.prime_season_start + "T00:00:00").getMonth(), 1);
    while (cur < end) {
      const range = monthRange(cur.getFullYear(), cur.getMonth(), selectedSeason.prime_season_start, selectedSeason.prime_season_end);
      if (range && range.nights > 0) {
        const hiltonBooked = bookedNightsInRange(activeForSeason, range.start, range.end, "hilton");
        const windsorBooked = bookedNightsInRange(activeForSeason, range.start, range.end, "windsor");
        const totalCabinNights = range.nights * 2;
        const totalBooked = hiltonBooked + windsorBooked;

        result.push({
          label: cur.toLocaleDateString("en-US", { month: "long" }),
          start: range.start,
          end: range.end,
          totalNights: range.nights,
          hiltonBooked,
          windsorBooked,
          availability: pct(totalCabinNights, totalBooked),
          hiltonAvailability: pct(range.nights, hiltonBooked),
          windsorAvailability: pct(range.nights, windsorBooked),
        });
      }
      cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
    }
    return result;
  }, [activeForSeason, selectedSeason]);

  const overall = useMemo(() => {
    if (!selectedSeason) return { availability: 0, hiltonAvailability: 0, windsorAvailability: 0, totalNights: 0, hiltonBooked: 0, windsorBooked: 0 };
    const totalNights = daysBetween(selectedSeason.prime_season_start, selectedSeason.prime_season_end);
    const hiltonBooked = bookedNightsInRange(activeForSeason, selectedSeason.prime_season_start, selectedSeason.prime_season_end, "hilton");
    const windsorBooked = bookedNightsInRange(activeForSeason, selectedSeason.prime_season_start, selectedSeason.prime_season_end, "windsor");
    return {
      availability: pct(totalNights * 2, hiltonBooked + windsorBooked),
      hiltonAvailability: pct(totalNights, hiltonBooked),
      windsorAvailability: pct(totalNights, windsorBooked),
      totalNights,
      hiltonBooked,
      windsorBooked,
    };
  }, [activeForSeason, selectedSeason]);

  const weekendStats = useMemo(() => {
    if (!selectedSeason) return null;
    const totalWeekendNights = countWeekendNights(selectedSeason.prime_season_start, selectedSeason.prime_season_end);
    const hiltonBooked = bookedWeekendNightsInRange(activeForSeason, selectedSeason.prime_season_start, selectedSeason.prime_season_end, "hilton");
    const windsorBooked = bookedWeekendNightsInRange(activeForSeason, selectedSeason.prime_season_start, selectedSeason.prime_season_end, "windsor");
    const totalBooked = hiltonBooked + windsorBooked;
    const totalAvailable = totalWeekendNights * 2;

    const byMonth = months.map((m) => {
      const mWeekends = countWeekendNights(m.start, m.end);
      const mHilton = bookedWeekendNightsInRange(activeForSeason, m.start, m.end, "hilton");
      const mWindsor = bookedWeekendNightsInRange(activeForSeason, m.start, m.end, "windsor");
      return {
        label: m.label,
        total: mWeekends,
        hiltonBooked: mHilton,
        windsorBooked: mWindsor,
        booked: mHilton + mWindsor,
        bookedPct: pct(mWeekends * 2, mHilton + mWindsor),
      };
    });

    return {
      totalWeekendNights,
      totalAvailable,
      totalBooked,
      hiltonBooked,
      windsorBooked,
      bookedPct: pct(totalAvailable, totalBooked),
      hiltonPct: pct(totalWeekendNights, hiltonBooked),
      windsorPct: pct(totalWeekendNights, windsorBooked),
      byMonth,
    };
  }, [activeForSeason, selectedSeason, months]);

  const shareholderStats = useMemo((): ShareholderStats[] => {
    if (!selectedSeason) return [];

    const shareMap = new Map<string, { name: string; priority: number }>();
    for (const sh of shareholders) {
      shareMap.set(sh.share, { name: sh.share_name || `${sh.first_name} ${sh.last_name}`, priority: sh.priority });
    }

    const byShare = new Map<string, Reservation[]>();
    for (const r of activeForSeason) {
      const list = byShare.get(r.share) || [];
      list.push(r);
      byShare.set(r.share, list);
    }

    const stats: ShareholderStats[] = [];
    const allShares = new Set([...shareMap.keys(), ...byShare.keys()]);

    for (const share of allShares) {
      const info = shareMap.get(share) || { name: share, priority: 99 };
      const resos = byShare.get(share) || [];

      const priorityStays = resos.filter((r) => {
        if (!isPriorityRound(r) || !selectedSeason) return false;
        // Only count if the reservation has nights within prime season
        const rStart = r.start_date > selectedSeason.prime_season_start ? r.start_date : selectedSeason.prime_season_start;
        const rEnd = r.end_date < selectedSeason.prime_season_end ? r.end_date : selectedSeason.prime_season_end;
        return rEnd > rStart;
      }).length;
      let totalNights = 0;
      let hiltonNights = 0;
      let windsorNights = 0;

      const monthStats = months.map((m) => {
        const hN = bookedNightsInRange(resos, m.start, m.end, "hilton");
        const wN = bookedNightsInRange(resos, m.start, m.end, "windsor");
        return { label: m.label, nights: hN + wN, hiltonNights: hN, windsorNights: wN };
      });

      if (selectedSeason) {
        totalNights = bookedNightsInRange(resos, selectedSeason.prime_season_start, selectedSeason.prime_season_end);
        hiltonNights = bookedNightsInRange(resos, selectedSeason.prime_season_start, selectedSeason.prime_season_end, "hilton");
        windsorNights = bookedNightsInRange(resos, selectedSeason.prime_season_start, selectedSeason.prime_season_end, "windsor");
      } else {
        for (const r of resos) {
          totalNights += nightCount(r);
          hiltonNights += cabinNights(r, "hilton");
          windsorNights += cabinNights(r, "windsor");
        }
      }

      stats.push({
        name: `${info.name} - ${info.priority}`,
        priority: info.priority,
        priorityStays,
        totalNights,
        hiltonNights,
        windsorNights,
        months: monthStats,
      });
    }

    stats.sort((a, b) => a.priority - b.priority);
    return stats;
  }, [activeForSeason, selectedSeason, shareholders, months]);

  const seasonTotals = useMemo(() => {
    const paidRevenue = activeForSeason
      .filter((r) => r.status === "paid")
      .reduce((sum, r) => sum + r.cost, 0);
    const pendingRevenue = activeForSeason
      .filter((r) => r.status !== "paid")
      .reduce((sum, r) => sum + r.cost, 0);
    const totalNights = selectedSeason
      ? bookedNightsInRange(activeForSeason, selectedSeason.prime_season_start, selectedSeason.prime_season_end)
      : activeForSeason.reduce((sum, r) => sum + nightCount(r), 0);
    return { paidRevenue, pendingRevenue, totalNights, totalReservations: activeForSeason.length };
  }, [activeForSeason, selectedSeason]);

  const totalsRow = useMemo(() => {
    const totalNights = shareholderStats.reduce((s, sh) => s + sh.totalNights, 0);
    const hiltonNights = shareholderStats.reduce((s, sh) => s + sh.hiltonNights, 0);
    const windsorNights = shareholderStats.reduce((s, sh) => s + sh.windsorNights, 0);
    const monthTotals = months.map((m, i) => ({
      label: m.label,
      nights: shareholderStats.reduce((s, sh) => s + (sh.months[i]?.nights || 0), 0),
      hiltonNights: shareholderStats.reduce((s, sh) => s + (sh.months[i]?.hiltonNights || 0), 0),
      windsorNights: shareholderStats.reduce((s, sh) => s + (sh.months[i]?.windsorNights || 0), 0),
    }));
    return { totalNights, hiltonNights, windsorNights, months: monthTotals };
  }, [shareholderStats, months]);

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="spinner" />
      </div>
    );
  }

  if (error) {
    return <div className="admin-section admin-error">{error}</div>;
  }

  const year = selectedSeason?.year || new Date().getFullYear();
  const cabinAvailability = (cabin: CabinType) =>
    cabin === "hilton" ? "hiltonAvailability" : "windsorAvailability" as const;

  return (
    <div className="admin-section analytics-section">
      <div className="admin-section-header">
        <h2>Analytics — {year} High Season</h2>
        {sortedSeasons.length > 1 && (
          <div className="analytics-year-selector">
            {sortedSeasons.map((s) => (
              <button
                key={s.year}
                type="button"
                className={`analytics-year-btn${s.year === year ? " active" : ""}`}
                onClick={() => setSelectedYear(s.year)}
              >
                {s.year}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="analytics-cards">
        <div className="analytics-card analytics-card-primary">
          <span className="analytics-card-label">Total Availability</span>
          <span className="analytics-card-value">{overall.availability}%</span>
          <span className="analytics-card-detail">
            {overall.totalNights * 2 - overall.hiltonBooked - overall.windsorBooked} of {overall.totalNights * 2} cabin-nights open
          </span>
        </div>
        {months.map((m) => (
          <div key={m.label} className="analytics-card">
            <span className="analytics-card-label">{m.label}</span>
            <span className="analytics-card-value">{m.availability}%</span>
            <span className="analytics-card-detail">
              {m.totalNights * 2 - m.hiltonBooked - m.windsorBooked} of {m.totalNights * 2} open
            </span>
          </div>
        ))}
      </div>

      <div className="overview-totals">
        <div className="overview-total-item">
          <span className="overview-total-value">{seasonTotals.totalReservations}</span>
          <span className="overview-total-label">Reservations</span>
        </div>
        <div className="overview-total-item">
          <span className="overview-total-value">{seasonTotals.totalNights}</span>
          <span className="overview-total-label">Nights Booked</span>
        </div>
        <div className="overview-total-item">
          <span className="overview-total-value overview-total-paid">
            ${seasonTotals.paidRevenue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </span>
          <span className="overview-total-label">Collected</span>
        </div>
        {seasonTotals.pendingRevenue > 0 && (
          <div className="overview-total-item">
            <span className="overview-total-value overview-total-pending">
              ${seasonTotals.pendingRevenue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </span>
            <span className="overview-total-label">Pending</span>
          </div>
        )}
      </div>

      <div className="analytics-cabin-grid">
        {CABINS.map(({ key, label }) => (
          <div key={key} className="analytics-cabin-section">
            <h3 className={`analytics-cabin-title ${key}`}>{label}</h3>
            <div className="analytics-cards">
              <div className="analytics-card analytics-card-primary">
                <span className="analytics-card-label">{label} Availability</span>
                <span className="analytics-card-value">{overall[cabinAvailability(key)]}%</span>
                <span className="analytics-card-detail">
                  {overall.totalNights - (key === "hilton" ? overall.hiltonBooked : overall.windsorBooked)} of {overall.totalNights} nights open
                </span>
              </div>
              {months.map((m) => (
                <div key={m.label} className="analytics-card">
                  <span className="analytics-card-label">{m.label}</span>
                  <span className="analytics-card-value">{m[cabinAvailability(key)]}%</span>
                  <span className="analytics-card-detail">
                    {m.totalNights - (key === "hilton" ? m.hiltonBooked : m.windsorBooked)} of {m.totalNights} open
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {weekendStats && (
        <div className="overview-panel analytics-weekend-panel">
          <h3 className="overview-panel-label">Weekend Availability</h3>
          <div className="analytics-cards">
            <div className="analytics-card analytics-card-primary">
              <span className="analytics-card-label">Weekend Availability</span>
              <span className="analytics-card-value">{weekendStats.bookedPct}%</span>
              <span className="analytics-card-detail">
                {weekendStats.totalAvailable - weekendStats.totalBooked} of {weekendStats.totalAvailable} nights open
              </span>
            </div>
            {CABINS.map(({ key, label }) => {
              const booked = key === "hilton" ? weekendStats.hiltonBooked : weekendStats.windsorBooked;
              const avail = key === "hilton" ? weekendStats.hiltonPct : weekendStats.windsorPct;
              return (
                <div key={key} className="analytics-card">
                  <span className="analytics-card-label">{label}</span>
                  <span className="analytics-card-value">{avail}%</span>
                  <span className="analytics-card-detail">
                    {weekendStats.totalWeekendNights - booked} of {weekendStats.totalWeekendNights} nights open
                  </span>
                </div>
              );
            })}
            {weekendStats.byMonth.map((m) => (
              <div key={m.label} className="analytics-card">
                <span className="analytics-card-label">{m.label}</span>
                <span className="analytics-card-value">{m.bookedPct}%</span>
                <span className="analytics-card-detail">
                  {m.total * 2 - m.booked} of {m.total * 2} nights open
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="overview-panel analytics-table-panel">
        <h3 className="overview-panel-label">Shareholder Breakdown</h3>
        <div className="admin-table-container">
          <table className="admin-table analytics-table">
            <thead>
              <tr>
                <th>Shareholder</th>
                <th className="analytics-num">Priority Stays</th>
                <th className="analytics-num">Total Nights</th>
                <th className="analytics-num">Hilton Nights</th>
                <th className="analytics-num">Windsor Nights</th>
                {months.map((m) => (
                  <th key={m.label} className="analytics-num">{m.label} Nights</th>
                ))}
                {months.map((m) => (
                  <th key={`h-${m.label}`} className="analytics-num analytics-sub-col">{m.label} Hilton</th>
                ))}
                {months.map((m) => (
                  <th key={`w-${m.label}`} className="analytics-num analytics-sub-col">{m.label} Windsor</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shareholderStats.map((sh) => (
                <tr key={sh.name}>
                  <td className="analytics-shareholder-name">{sh.name}</td>
                  <td className="analytics-num">{sh.priorityStays}</td>
                  <td className="analytics-num">{sh.totalNights}</td>
                  <td className="analytics-num">{sh.hiltonNights}</td>
                  <td className="analytics-num">{sh.windsorNights}</td>
                  {sh.months.map((m) => (
                    <td key={m.label} className="analytics-num">{m.nights}</td>
                  ))}
                  {sh.months.map((m) => (
                    <td key={`h-${m.label}`} className="analytics-num analytics-sub-col">{m.hiltonNights}</td>
                  ))}
                  {sh.months.map((m) => (
                    <td key={`w-${m.label}`} className="analytics-num analytics-sub-col">{m.windsorNights}</td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="analytics-totals-row">
                <td className="analytics-shareholder-name">TOTAL</td>
                <td className="analytics-num"></td>
                <td className="analytics-num">{totalsRow.totalNights}</td>
                <td className="analytics-num">{totalsRow.hiltonNights}</td>
                <td className="analytics-num">{totalsRow.windsorNights}</td>
                {totalsRow.months.map((m) => (
                  <td key={m.label} className="analytics-num">{m.nights}</td>
                ))}
                {totalsRow.months.map((m) => (
                  <td key={`h-${m.label}`} className="analytics-num analytics-sub-col">{m.hiltonNights}</td>
                ))}
                {totalsRow.months.map((m) => (
                  <td key={`w-${m.label}`} className="analytics-num analytics-sub-col">{m.windsorNights}</td>
                ))}
              </tr>
              <tr className="analytics-available-row">
                <td className="analytics-shareholder-name" colSpan={2}>Total Available Reservation Nights</td>
                <td className="analytics-num">{overall.totalNights * 2}</td>
                <td className="analytics-num">{overall.totalNights}</td>
                <td className="analytics-num">{overall.totalNights}</td>
                {months.map((m) => (
                  <td key={m.label} className="analytics-num">{m.totalNights * 2}</td>
                ))}
                {months.map((m) => (
                  <td key={`h-${m.label}`} className="analytics-num analytics-sub-col">{m.totalNights}</td>
                ))}
                {months.map((m) => (
                  <td key={`w-${m.label}`} className="analytics-num analytics-sub-col">{m.totalNights}</td>
                ))}
              </tr>
              <tr className="analytics-pct-row">
                <td className="analytics-shareholder-name" colSpan={2}>% Availability</td>
                <td className="analytics-num">{overall.availability}%</td>
                <td className="analytics-num">{overall.hiltonAvailability}%</td>
                <td className="analytics-num">{overall.windsorAvailability}%</td>
                {months.map((m) => (
                  <td key={m.label} className="analytics-num">{m.availability}%</td>
                ))}
                {months.map((m) => (
                  <td key={`h-${m.label}`} className="analytics-num analytics-sub-col">{m.hiltonAvailability}%</td>
                ))}
                {months.map((m) => (
                  <td key={`w-${m.label}`} className="analytics-num analytics-sub-col">{m.windsorAvailability}%</td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}
